import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/store/authStore';
import { useCartStore } from '@/store/cartStore';
import { parkCartForRecovery } from '@/lib/utils/session';
import { ROUTES } from '@/lib/config/routes';

const IDLE_TIMEOUT_MS = 15 * 60 * 1000;

export const MAX_SESSION_MS = 8 * 60 * 60 * 1000;

export const SESSION_WARNING_LEAD_MS = 5 * 60 * 1000;

const THROTTLE_MS = 1000;

const CHECK_INTERVAL_MS = 15 * 1000;

const LAST_ACTIVITY_KEY = 'db_scaffolding.last_activity';

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'click'] as const;

export function useIdleLogout(enabled: boolean): void {
  const navigate = useNavigate();
  const lastActivityRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;

    const logout = (reason: string) => {
      localStorage.removeItem(LAST_ACTIVITY_KEY);
      const parked = parkCartForRecovery();
      useAuthStore.getState().clearSession();
      useCartStore.getState().clear();
      toast.error(parked ? `${reason}. Your sale was parked in Held Sales.` : reason);
      navigate(ROUTES.login, { replace: true });
    };

    const idleFor = () => {
      const last = Number(localStorage.getItem(LAST_ACTIVITY_KEY)) || lastActivityRef.current;
      return Date.now() - last;
    };

    const sessionAge = () => {
      const startedAt = useAuthStore.getState().sessionStartedAt;
      return startedAt ? Date.now() - startedAt : 0;
    };

    const check = () => {
      if (sessionAge() >= MAX_SESSION_MS) {
        logout('Session expired, please sign in again');
      } else if (idleFor() >= IDLE_TIMEOUT_MS) {
        logout('Signed out due to inactivity');
      }
    };

    const markActivity = () => {
      const now = Date.now();
      if (now - lastActivityRef.current < THROTTLE_MS) return;
      lastActivityRef.current = now;
      localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') check();
    };

    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, markActivity, { passive: true }));
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', check);
    window.addEventListener('pageshow', check);

    markActivity();
    const interval = setInterval(check, CHECK_INTERVAL_MS);

    return () => {
      clearInterval(interval);
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, markActivity));
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', check);
      window.removeEventListener('pageshow', check);
    };
  }, [enabled, navigate]);
}
